import React, { useEffect, useMemo, useState } from "react";

const API_BASE_URL = "";

const EXPERIENCES = [
  {
    pageKey: "private-yacht-charter-mumbai-goa",
    pageName: "Private Yacht",
    enabled: true,
  },
  {
    pageKey: "yacht-party-mumbai",
    pageName: "Yacht Parties",
    enabled: true,
  },
  {
    pageKey: "SpeedBoat",
    pageName: "Speed Boat",
    enabled: false,
  },
  {
    pageKey: "CorporateEvents",
    pageName: "Corporate",
    enabled: false,
  },
];

const emptyForm = {
  pageKey: "private-yacht-charter-mumbai-goa",
  cardId: "",
  timeSlotId: "",
  price: "",
};

const YachtDetailsCms = () => {
  const [boats, setBoats] = useState([]);
  const [slots, setSlots] = useState([]);
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const getToken = () => localStorage.getItem("indiaboatsAdminToken");

  const authHeaders = () => ({
    "Content-Type": "application/json",
    Authorization: `Bearer ${getToken()}`,
  });

  const boatsForExperience = useMemo(
    () => boats.filter((boat) => boat.pageKey === form.pageKey),
    [boats, form.pageKey]
  );

  const selectedExperience = EXPERIENCES.find(
    (item) => item.pageKey === form.pageKey
  );

  const fetchJson = async (url, options) => {
    const response = await fetch(url, options);
    const result = await response.json().catch(() => ({}));

    if (!response.ok) {
      throw new Error(result.message || "Request failed");
    }

    return result;
  };

  const loadAll = async () => {
    const [boatsResult, slotsResult, pricesResult] = await Promise.all([
      fetch(`${API_BASE_URL}/api/yacht-details/boats`).then((res) =>
        res.ok ? res.json() : []
      ),
      fetch(`${API_BASE_URL}/api/time-slots/slots`).then((res) =>
        res.ok ? res.json() : []
      ),
      fetch(`${API_BASE_URL}/api/yacht-prices`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      }).then((res) => (res.ok ? res.json() : [])),
    ]);

    setBoats(Array.isArray(boatsResult) ? boatsResult : []);
    setSlots(Array.isArray(slotsResult) ? slotsResult : []);
    setRows(Array.isArray(pricesResult) ? pricesResult : []);
  };

  useEffect(() => {
    const load = async () => {
      try {
        await loadAll();
      } catch (error) {
        console.error(error);
        alert("Failed to load yacht prices");
      } finally {
        setLoading(false);
      }
    };

    load();
  }, []);

  const resetForm = (pageKey = form.pageKey) => {
    setEditingId(null);
    setForm({
      ...emptyForm,
      pageKey,
    });
  };

  const handleExperienceChange = (pageKey) => {
    const experience = EXPERIENCES.find((item) => item.pageKey === pageKey);

    if (experience && !experience.enabled) {
      alert("Speed Boat and Corporate pricing will be added next.");
      return;
    }

    resetForm(pageKey);
  };

  const handleSave = async () => {
    if (!selectedExperience?.enabled) {
      alert("This experience is not enabled yet");
      return;
    }

    if (!form.cardId || !form.timeSlotId || form.price === "") {
      alert("Please select boat, time slot and price");
      return;
    }

    const boat = boats.find(
      (item) => String(item.cardId) === String(form.cardId)
    );

    try {
      setSaving(true);

      const payload = {
        pageKey: form.pageKey,
        pageName: selectedExperience?.pageName || "",
        cardId: form.cardId,
        boatTitle: boat?.title || boat?.label || "",
        timeSlotId: form.timeSlotId,
        price: Number(form.price),
      };

      const url = editingId
        ? `${API_BASE_URL}/api/yacht-prices/${editingId}`
        : `${API_BASE_URL}/api/yacht-prices`;

      await fetchJson(url, {
        method: editingId ? "PUT" : "POST",
        headers: authHeaders(),
        body: JSON.stringify(payload),
      });

      await loadAll();
      resetForm();
      alert(editingId ? "Price updated" : "Price saved");
    } catch (error) {
      alert(error.message || "Failed to save price");
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (row) => {
    setEditingId(row._id);
    setForm({
      pageKey: row.pageKey || emptyForm.pageKey,
      cardId: String(row.cardId || ""),
      timeSlotId: String(row.timeSlotId?._id || row.timeSlotId || ""),
      price: row.price ?? "",
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleDelete = async (row) => {
    if (!window.confirm("Delete this yacht price?")) {
      return;
    }

    try {
      setSaving(true);
      await fetchJson(`${API_BASE_URL}/api/yacht-prices/${row._id}`, {
        method: "DELETE",
        headers: authHeaders(),
      });
      await loadAll();

      if (editingId === row._id) {
        resetForm();
      }
    } catch (error) {
      alert(error.message || "Failed to delete price");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="cms-page">
        <div className="cms-page-loading">Loading...</div>
      </div>
    );
  }

  return (
    <div className="cms-page">
      <div className="cms-page-header">
        <div>
          <h1>Yacht Prices</h1>
          <p>
            Choose an experience, boat, time slot and price. Private Yacht and
            Yacht Parties are available now.
          </p>
        </div>
      </div>

      <div className="cms-section-card">
        <div className="cms-section-card-header">
          <h2>{editingId ? "Update Price" : "Assign Price"}</h2>
          <p>
            Create time slots in Boat Time Management first, then price them
            here for each boat.
          </p>
        </div>

        <div className="cms-form-grid cms-form-grid-4">
          <div className="cms-form-group">
            <label>Yacht Experience</label>
            <select
              value={form.pageKey}
              onChange={(e) => handleExperienceChange(e.target.value)}
            >
              {EXPERIENCES.map((item) => (
                <option key={item.pageKey} value={item.pageKey}>
                  {item.pageName}
                  {item.enabled ? "" : " (coming soon)"}
                </option>
              ))}
            </select>
          </div>

          <div className="cms-form-group">
            <label>Boat Title</label>
            <select
              value={form.cardId}
              onChange={(e) =>
                setForm((previous) => ({
                  ...previous,
                  cardId: e.target.value,
                }))
              }
            >
              <option value="">Select boat</option>
              {boatsForExperience.map((boat) => (
                <option key={String(boat.cardId)} value={String(boat.cardId)}>
                  {boat.title || boat.label}
                </option>
              ))}
            </select>
          </div>

          <div className="cms-form-group">
            <label>Time Slot</label>
            <select
              value={form.timeSlotId}
              onChange={(e) =>
                setForm((previous) => ({
                  ...previous,
                  timeSlotId: e.target.value,
                }))
              }
            >
              <option value="">Select time slot</option>
              {slots.map((slot) => (
                <option key={slot._id} value={slot._id}>
                  {slot.label}
                </option>
              ))}
            </select>
          </div>

          <div className="cms-form-group">
            <label>Price (₹)</label>
            <input
              type="number"
              min="0"
              value={form.price}
              onChange={(e) =>
                setForm((previous) => ({
                  ...previous,
                  price: e.target.value,
                }))
              }
              placeholder="e.g. 15000"
            />
          </div>
        </div>

        <div className="cms-bottom-actions">
          <button
            type="button"
            className="cms-save-btn"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? "Saving..." : editingId ? "Save Changes" : "Save Price"}
          </button>
          {editingId ? (
            <button
              type="button"
              className="cms-upload-btn"
              onClick={() => resetForm()}
              disabled={saving}
            >
              Cancel Update
            </button>
          ) : null}
        </div>
      </div>

      <div className="cms-section-card">
        <div className="cms-section-card-header">
          <h2>Assigned Prices</h2>
          <p>These prices appear on the yacht details booking box.</p>
        </div>

        {rows.length === 0 ? (
          <p className="cms-inquiries-empty">No yacht prices assigned yet.</p>
        ) : (
          <div className="cms-enquiries-table-wrap">
            <table className="cms-enquiries-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Experience</th>
                  <th>Boat</th>
                  <th>Time Slot</th>
                  <th>Price</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, index) => (
                  <tr key={row._id}>
                    <td>{index + 1}</td>
                    <td>{row.pageName || row.pageKey}</td>
                    <td>{row.boatTitle || "—"}</td>
                    <td>
                      {row.timeSlotId?.label ||
                        (row.timeSlotId?.from
                          ? `${row.timeSlotId.from} - ${row.timeSlotId.to}`
                          : "—")}
                    </td>
                    <td>
                      {row.price != null
                        ? `₹ ${Number(row.price).toLocaleString("en-IN")}`
                        : "—"}
                    </td>
                    <td>
                      <div className="cms-item-row-actions">
                        <button
                          type="button"
                          className="cms-upload-btn"
                          onClick={() => startEdit(row)}
                        >
                          Update
                        </button>
                        <button
                          type="button"
                          className="cms-delete-btn"
                          onClick={() => handleDelete(row)}
                          disabled={saving}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default YachtDetailsCms;
